import type React from 'react';
import { useState } from 'react';

import { DetailsTravelItem, DetailsTravelResponse } from '@/app/models/detailsTravel';
import { useUndoUnionTravelMutation } from '@/app/services/detailsTravelApi';
import { formatDate } from '@/app/utils/dateUtils';
import formatterMonetaryValue from '@/app/utils/numberUtils';
import DataTable, { bodyCellSx, Column } from '@/components/common/DataTable';
import TruncatedTextWithTooltip from '@/components/common/truncatedTextWithTooltip';
import { usePeriod } from '@/context/PeriodContext';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import LinkIcon from '@mui/icons-material/Link';
import ListAltIcon from '@mui/icons-material/ListAlt';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, Checkbox, IconButton, Menu, MenuItem, TableCell, Tooltip, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

interface TavelTableProps {
  selectTravel: number[];
  setSelectTravel: (id: number[]) => void;
  setDrawerEdit: CallableFunction;
  setDrawerTransfer: CallableFunction;
  setSelectedTravelItem: (id: DetailsTravelItem) => void;
  selectTravelItem: DetailsTravelItem;
  equipmentData: DetailsTravelResponse | undefined;
}

export default function TavelTable({
  selectTravel,
  setSelectTravel,
  setDrawerEdit,
  setDrawerTransfer,
  setSelectedTravelItem,
  selectTravelItem,
  equipmentData,
}: TavelTableProps) {
  const theme = useTheme();
  const period = usePeriod();

  const [selectedStatus, setSelectedStatus] = useState<number | null>(null);
  const [idUndoUnion, setIdUndoUnion] = useState<number | null>(null);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const { t } = useTranslation(['travel', 'common']);

  const handleCheckboxChange = (id: number, checked: boolean) => {
    const newSelectedCollaborators = checked
      ? [...selectTravel, id]
      : selectTravel.filter((collabId) => collabId !== id);

    setSelectTravel(newSelectedCollaborators);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allIds = equipmentData?.items?.map((collab) => collab.id);
      setSelectTravel(allIds as number[]);
    } else {
      setSelectTravel([]);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, id: number) => {
    setAnchorEl(event.currentTarget);
    setSelectedStatus(id);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDrowersEdit = () => {
    setDrawerEdit(true);
    setAnchorEl(null);
  };

  const handleDrowersTrasnfer = () => {
    setDrawerTransfer(true);
    setAnchorEl(null);
  };

  const [undoUnion] = useUndoUnionTravelMutation();

  const handleUndoUnion = async () => {
    await toast.promise(
      undoUnion({
        id: idUndoUnion as number,
        period: period.selectedPeriod?.date as string,
      }).unwrap(),
      {
        pending: t('common:processing.wait.a.moment') as string,
        success: t('common:undo.union.success') as string,
        error: t('common:processing.failed') as string,
      },
      {
        position: toast.POSITION.TOP_RIGHT,
      },
    );
    setDrawerEdit(false);
    setAnchorEl(null);
  };

  const columns: Column[] = [
    {
      key: 'select',
      label: (
        <Checkbox
          color="secondary"
          sx={{ p: 0 }}
          indeterminate={selectTravel.length > 0 && selectTravel.length < (equipmentData?.items?.length as number)}
          checked={(equipmentData?.items?.length as number) > 0 && selectTravel.length === equipmentData?.items?.length}
          onChange={handleSelectAll}
          inputProps={{ 'aria-label': 'select all collaborators' }}
        />
      ),
      width: theme.spacing(6),
    },
    { key: 'collaborator', label: t('common:collaborator'), width: theme.spacing(50) },
    { key: 'local', label: t('local') },
    { key: 'value', label: t('common:total.value') },
    { key: 'period', label: t('common:period') },
    { key: 'records', label: t('common:records') },
    { key: 'consolidated', label: t('common:consolidated'), align: 'center' },
    { key: 'grouped', label: t('common:grouped'), align: 'center' },
    { key: 'actions', label: t('common:actions'), align: 'right' },
  ];

  const renderRow = (item: DetailsTravelItem) => {
    const isSelected = selectTravel.includes(item.id);
    return (
      <>
        <TableCell sx={{ ...bodyCellSx, padding: 'checkbox' }}>
          <Checkbox
            color="secondary"
            sx={{ p: 0 }}
            checked={isSelected}
            onChange={(e) => handleCheckboxChange(item.id, e.target.checked)}
            inputProps={{ 'aria-labelledby': `Item-${item.id}` }}
          />
        </TableCell>

        <TableCell sx={bodyCellSx}>
          <TruncatedTextWithTooltip
            value={`${item.person ? item.person.personBadge : '---'} - ${item.person ? item.person.name : '---'}`}
            sx={bodyCellSx}
            size={40}
          />
        </TableCell>

        <TableCell sx={{ ...bodyCellSx }}>{item.local}</TableCell>

        <TableCell sx={{ ...bodyCellSx }}>{formatterMonetaryValue(item.value)}</TableCell>
        <TableCell sx={{ ...bodyCellSx }}>{`${item.initDate ? formatDate(item.initDate) : '---'} - 
        ${item.endDate ? formatDate(item.endDate as string) : '---'}`}</TableCell>

        <TableCell sx={{ ...bodyCellSx }}>{item.objective ? item.objective : '----'}</TableCell>

        <TableCell sx={{ ...bodyCellSx, textAlign: 'center' }}>
          {item.consolidated ? (
            <CheckIcon sx={{ color: theme.palette.success.light, cursor: 'pointer' }} />
          ) : (
            <CloseIcon sx={{ color: theme.palette.grey[500], cursor: 'pointer' }} />
          )}
        </TableCell>

        <TableCell sx={{ ...bodyCellSx, textAlign: 'center' }}>
          {item.grouped ? (
            <Tooltip title={t('common:union.result')} arrow placement="bottom-start">
              <LinkIcon sx={{ color: theme.palette.secondary.light, cursor: 'pointer' }} />
            </Tooltip>
          ) : null}
        </TableCell>
        <TableCell sx={{ ...bodyCellSx, textAlign: 'right' }}>
          <IconButton
            size="small"
            onClick={(e) => {
              handleMenuOpen(e, item.id);
              setSelectedTravelItem(item);
              setIdUndoUnion(item.id);
            }}
            aria-label="more actions"
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </TableCell>
      </>
    );
  };

  // Use the project ID as the unique key
  const getRowKey = (project: DetailsTravelItem) => project.id as number;

  return (
    <>
      <DataTable
        columns={columns}
        data={equipmentData?.items || []}
        renderRow={renderRow}
        getRowKey={getRowKey}
        emptyMessage={t('common:no.data.for.the.chosen.period.and.search.term')}
        size="small"
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {selectedStatus && (
          <>
            <MenuItem onClick={() => handleDrowersEdit()}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ListAltIcon />
                {t('common:edit.detail')}
              </Box>
            </MenuItem>
            <MenuItem onClick={() => handleDrowersTrasnfer()}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CompareArrowsIcon />
                {t('common:transfer')}
              </Box>
            </MenuItem>
            {selectTravelItem.grouped && (
              <MenuItem onClick={() => handleUndoUnion()}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CallSplitIcon />
                  {t('common:undo.union')}
                </Box>
              </MenuItem>
            )}
          </>
        )}
      </Menu>
    </>
  );
}
